import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';
import PageTitle from '../layout/PageTitle';
import BlogImage from '../ui/BlogImage';

interface BlogPost {
  id: string;
  title: string;
  description: string;
  date: string;
  category: string;
  readTime: string;
  image: string;
  slug: string;
}

const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: "How SentiHype Detected Opendoor's Meme Stock Surge Before It Exploded",
    description: "In July 2025, Opendoor (ticker: $OPEN) became one of the most dramatic meme-stock stories of the year. SentiHype detected the spike in Reddit activity around $OPEN before the price went vertical.",
    date: "2025-07-28",
    category: "Case Study",
    readTime: "5 min read",
    image: "/blog-opendoor.jpg",
    slug: "opendoor"
  }
];

const BlogList: React.FC = () => {
  return (
    <div className="content">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
      <PageTitle title="Blog - SentiHype" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              SentiHype Blog
            </h1>
            <p className="text-xl text-[#bbb] max-w-2xl mx-auto">
              Real-time insights into social sentiment and market movements
            </p>
          </div>

          {/* Featured Article */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-6">Featured Article</h2>
            <motion.div
              whileHover={{ y: -4 }}
              className="bg-[#1a1a1a] border border-[#333] rounded-xl overflow-hidden shadow-lg"
            >
              <Link to="/blog/opendoor" className="block">
                <div className="md:flex">
                  <div className="md:w-1/3 flex items-center">
                    <BlogImage />
                  </div>
                  <div className="md:w-2/3 p-6">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="px-3 py-1 bg-[#4ff6e6]/10 text-[#4ff6e6] text-sm rounded-full border border-[#4ff6e6]/20">
                        Case Study
                      </span>
                      <span className="text-[#bbb] text-sm">Featured</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3 leading-tight">
                      How SentiHype Detected Opendoor's Meme Stock Surge Before It Exploded
                    </h3>
                    <p className="text-[#ccc] mb-4 leading-relaxed">
                      In July 2025, Opendoor (ticker: $OPEN) became one of the most dramatic meme-stock stories of the year. 
                      SentiHype detected the spike in Reddit activity around $OPEN before the price went vertical.
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-[#bbb]">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>July 28, 2025</span>
                        </div>
                        <span>5 min read</span>
                      </div>
                      <ArrowRight className="w-5 h-5 text-[#4ff6e6]" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>

          {/* All Articles */}
          <div>
            <h2 className="text-2xl font-semibold text-white mb-6">All Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -4 }}
                  className="bg-[#1a1a1a] border border-[#333] rounded-xl overflow-hidden shadow-lg"
                >
                                     <Link to={`/blog/${post.slug}`} className="block">
                     <BlogImage />
                    <div className="p-6">
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded-full border border-[#D4AF37]/20">
                          {post.category}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-3 leading-tight line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-[#ccc] text-sm mb-4 leading-relaxed line-clamp-3">
                        {post.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-xs text-[#bbb]">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                          <span>{post.readTime}</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-[#4ff6e6]" />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>

          {/* Newsletter Signup */}
          <div className="bg-gradient-to-r from-[#4ff6e6]/10 to-[#33e6d6]/10 border border-[#4ff6e6]/20 rounded-lg p-8 mt-12">
            <div className="text-center">
              <h3 className="text-2xl font-semibold text-white mb-4">
                Stay Updated with Market Insights
              </h3>
              <p className="text-[#ccc] mb-6 max-w-md mx-auto">
                Get the latest articles about social sentiment analysis and market movements delivered to your inbox.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="input-glass flex-1 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ff6e6] focus:border-transparent"
                />
                <button className="btn btn-gold">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </motion.div>
    </div>
    </div>
  );
};

export default BlogList; 
